import { useAuthContext } from "../../auth/state/AuthContext";
import HeaderTechform from "../../shared/app-shell/HeaderTechform";
import PageFooter from "../../shared/app-shell/PageFooter";
import Sidebar from "../../shared/app-shell/Sidebar";
import logo from "../../shared/assets/Vector.svg";
import { ReactComponent as CustomIcon } from "../../shared/assets/disk.svg";
import { themeAtom } from "../../shared/atoms";
import { toastSuccess } from "../../shared/components/ToastContainer";
import { log, logV } from "../../shared/services/debug-logger";
import StepIndicator from "../../tech-form/components/CompletedstepIndicator";
import { techFormStepCountAtom } from "../../tech-form/state/tech-form.atoms";
import {
  saveBq10Miscellaneous,
  saveBq1GenInfo,
  saveBq2Certification,
  saveBq3CrewManagement,
  saveBq4SafetyManagement,
  saveBq5CargoArrangements,
  saveBq6CargoGear,
  saveBq7Container,
  saveBq8Mooring,
  saveBq9EngineRoom,
} from "../services/baltic-forms.service";
import {
  bqF10MiscellaneousAtom,
  bqF1GeneralInformationAtom,
  bqF2CertificationAtom,
  bqF3CrewManagementAtom,
  bqF4SafetyManagementAtom,
  bqF5CargoArrangementsAtom,
  bqF6CargoGearAtom,
  bqF7ContainerAtom,
  bqF8MooringAtom,
  bqF9EngineRoomAtom,
} from "../state/baltic-form.atoms";
import { Bq1GeneralInformation } from "./Bq1GeneralInformation";
import { Bq2Certification } from "./Bq2Certification";
import { Bq3CrewManagement } from "./Bq3CrewManagement";
import { Bq4SafetyManagement } from "./Bq4SafetyManagement";
import { Bq5CargoArrangements } from "./Bq5CargoArrangements";
import { Bq6CargoGear } from "./Bq6CargoGear";
import { Bq7Container } from "./Bq7Container";
import { Bq8Mooring } from "./Bq8Mooring";
import { Bq9EngineRoom } from "./Bq9EngineRoom";
import { Bq10Miscellaneous } from "./Bq10Miscellaneous";
import { Bq11Confirmation } from "./Bq11Confirmation";
import { datadogRum } from "@datadog/browser-rum";
import { useAtomValue, useSetAtom } from "jotai";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { twMerge } from "tailwind-merge";

interface BalticQuestionnaireeProps {
  isCompletedForm: boolean;
  routeBasePath?: string;
  landingPagePath?: string;
}

let _prevForm: any;

export const BalticQuestionnaire: React.FC<BalticQuestionnaireeProps> = ({
  isCompletedForm,
  routeBasePath,
  landingPagePath,
}) => {
  const theme = useAtomValue(themeAtom);
  const setTechFormStepCount = useSetAtom(techFormStepCountAtom);
  const bqF1GeneralInformation = useAtomValue(bqF1GeneralInformationAtom);
  const bqF2Certification = useAtomValue(bqF2CertificationAtom);
  const bqF3CrewManagement = useAtomValue(bqF3CrewManagementAtom);
  const bqF4SafetyManagement = useAtomValue(bqF4SafetyManagementAtom);
  const bqF5CargoArrangements = useAtomValue(bqF5CargoArrangementsAtom);
  const bqF6CargoGear = useAtomValue(bqF6CargoGearAtom);
  const bqF7Container = useAtomValue(bqF7ContainerAtom);
  const bqF8Mooring = useAtomValue(bqF8MooringAtom);
  const bqF9EngineRoom = useAtomValue(bqF9EngineRoomAtom);
  const bqF10Miscellaneous = useAtomValue(bqF10MiscellaneousAtom);

  const totalSteps = 11;
  const { sub: email } = useAuthContext().state.user?.accessToken || {};
  const { pageName, vesselName, imoNumber } = useParams();
  const activeRouteBasePath = routeBasePath ?? (isCompletedForm ? "/balticForm/completed" : "/balticForm");

  const stepToPageMap: { [key: number]: string } = {
    1: "general-info",
    2: "certification",
    3: "crew-management",
    4: "safety-management",
    5: "cargo-arrangements",
    6: "cargo-gear",
    7: "container-bulkers",
    8: "mooring",
    9: "engine-room",
    10: "miscellaneous",
    11: "confirm-sub",
  };
  const pageToStepMap = Object.fromEntries(Object.entries(stepToPageMap).map(([k, v]) => [v, parseInt(k)]));

  const step = pageToStepMap[pageName || "general-info"] || 1;
  logV("BalticQuestionnaire - BUILD", { step, completed: isCompletedForm });

  const goToNext = () => {
    log("BalticQuestionnaire - goToNext", { step });
    if (!isCompletedForm) {
      datadogRum.addAction("techFormNext", {
        ext_user_email: email,
        vessel_name: vesselName,
        imo_number: imoNumber,
        currentStep: step,
      });
    }

    saveBqFormIfEdited(step);

    const nextStep = step + 1;
    const nextPage = stepToPageMap[nextStep];

    if (nextPage) {
      navigate(`${activeRouteBasePath}/${imoNumber}/${vesselName}/${nextPage}`);
    }
  };

  const saveAndClose = () => {
    log("BalticQuestionnaire - saveAndClose", { step });
    setTechFormStepCount(step);

    toastSuccess("Form saved succesfully");
    setTimeout(() => {
      goToLandingPage();
    }, 3000);
  };

  const goToPrev = () => {
    log("BalticQuestionnaire - goToPrev", { step });
    saveBqFormIfEdited(step);

    const prevStep = step - 1;
    const prevPage = stepToPageMap[prevStep];

    if (prevPage) {
      navigate(`${activeRouteBasePath}/${imoNumber}/${vesselName}/${prevPage}`);
    }
  };

  const navigate = useNavigate();

  const goToLandingPage = () => {
    log("BalticQuestionnaire - goToLandingPage");
    navigate(landingPagePath ?? `/landingPage`);
  };

  const saveBqFormIfEdited = async (step: number) => {
    const bqFormsByStep: Record<number, any> = {
      1: bqF1GeneralInformation,
      2: bqF2Certification,
      3: bqF3CrewManagement,
      4: bqF4SafetyManagement,
      5: bqF5CargoArrangements,
      6: bqF6CargoGear,
      7: bqF7Container,
      8: bqF8Mooring,
      9: bqF9EngineRoom,
      10: bqF10Miscellaneous,
    };
    const _form = bqFormsByStep[step] ?? {};
    const storageisEmpty = Object.keys(_form).length === 0;
    const hasChanges = JSON.stringify(_prevForm) !== JSON.stringify(_form);

    if (!storageisEmpty && hasChanges) {
      if (step === 1) saveBq1GenInfo(imoNumber, vesselName, { bqF1GeneralInformation: _form, vesselName, imoNumber });
      if (step === 2) saveBq2Certification(imoNumber, vesselName, { bqF2Certification: _form, vesselName, imoNumber });
      if (step === 3)
        saveBq3CrewManagement(imoNumber, vesselName, { bqF3CrewManagement: _form, vesselName, imoNumber });
      if (step === 4)
        saveBq4SafetyManagement(imoNumber, vesselName, { bqF4SafetyManagement: _form, vesselName, imoNumber });
      if (step === 5)
        saveBq5CargoArrangements(imoNumber, vesselName, { bqF5CargoArrangements: _form, vesselName, imoNumber });
      if (step === 6) saveBq6CargoGear(imoNumber, vesselName, { bqF6CargoGear: _form, vesselName, imoNumber });
      if (step === 7) saveBq7Container(imoNumber, vesselName, { bqF7Container: _form, vesselName, imoNumber });
      if (step === 8) saveBq8Mooring(imoNumber, vesselName, { bqF8Mooring: _form, vesselName, imoNumber });
      if (step === 9) saveBq9EngineRoom(imoNumber, vesselName, { bqF9EngineRoom: _form, vesselName, imoNumber });
      if (step === 10)
        saveBq10Miscellaneous(imoNumber, vesselName, { bqF10Miscellaneous: _form, vesselName, imoNumber });
    }
  };

  const cacheInitialForm = (form: any) => {
    _prevForm = form;
  };

  const bqSection = () => {
    logV("BalticQuestionnaire - bqSection", { step });
    switch (step) {
      case 1:
        return (
          <Bq1GeneralInformation
            isCompletedForm={isCompletedForm}
            cacheInitialForm={cacheInitialForm}
            readonly={false}
          />
        );
      case 2:
        return (
          <Bq2Certification isCompletedForm={isCompletedForm} cacheInitialForm={cacheInitialForm} readonly={false} />
        );
      case 3:
        return (
          <Bq3CrewManagement isCompletedForm={isCompletedForm} cacheInitialForm={cacheInitialForm} readonly={false} />
        );
      case 4:
        return (
          <Bq4SafetyManagement isCompletedForm={isCompletedForm} cacheInitialForm={cacheInitialForm} readonly={false} />
        );
      case 5:
        return (
          <Bq5CargoArrangements
            isCompletedForm={isCompletedForm}
            cacheInitialForm={cacheInitialForm}
            readonly={false}
          />
        );
      case 6:
        return <Bq6CargoGear isCompletedForm={isCompletedForm} cacheInitialForm={cacheInitialForm} readonly={false} />;
      case 7:
        return <Bq7Container isCompletedForm={isCompletedForm} cacheInitialForm={cacheInitialForm} readonly={false} />;
      case 8:
        return <Bq8Mooring isCompletedForm={isCompletedForm} cacheInitialForm={cacheInitialForm} readonly={false} />;
      case 9:
        return <Bq9EngineRoom isCompletedForm={isCompletedForm} cacheInitialForm={cacheInitialForm} readonly={false} />;
      case 10:
        return (
          <Bq10Miscellaneous isCompletedForm={isCompletedForm} cacheInitialForm={cacheInitialForm} readonly={false} />
        );
      case 11:
        return (
          <Bq11Confirmation isCompletedForm={isCompletedForm} goToPrev={goToPrev} goToLandingPage={goToLandingPage} />
        );
      default:
        return null;
    }
  };

  return (
    <div className={twMerge("flex h-screen", theme.home.tailwind.backgound)}>
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden p-4">
        <HeaderTechform />
        <br />
        <ToastContainer />
        <header className="flex items-center justify-between">
          <div className="grid grid-flow-col grid-rows-2 gap-4">
            <div className="flex justify-start">
              <div>
                <img src={logo} alt="Description" className="mr-2" />
              </div>
              <div className={twMerge("mb-2 text-2xl font-bold md:mb-0 md:mr-4", theme.home.tailwind.textDefault)}>
                Baltic Questionnaire
              </div>
            </div>
            <div className="flex-col items-center md:flex">
              <StepIndicator currentStep={step} totalSteps={totalSteps} />
            </div>
          </div>
        </header>

        <main
          className={twMerge(
            "flex flex-1 flex-col overflow-y-auto border-y",
            "[&::-webkit-scrollbar]:w-2.5",
            "[&::-webkit-scrollbar-track]:bg-transparent",
            "[&::-webkit-scrollbar-thumb]:rounded-full",
            theme.uwhcAssistant.tableScroll,
            theme.uwhcAssistant.scrolHover,
            theme.uwhcAssistant.border,
          )}
        >
          {bqSection()}
        </main>
        {step !== 11 && (
          <footer className="flex justify-between p-4">
            {!isCompletedForm && (
              <button
                onClick={saveAndClose}
                className={twMerge(
                  "flex items-center rounded border px-4 py-2",
                  theme.uwhcAssistant.inputBorder,
                  theme.uwhcAssistant.inputBg,
                  theme.uwhcAssistant.text,
                )}
              >
                <CustomIcon className={twMerge("mr-2 h-6 w-6", theme.vesselDetails.info)} />
                <span>Save and Close</span>
              </button>
            )}

            <div className="grid grid-cols-2 gap-2">
              {step > 1 && (
                <button
                  className={twMerge(
                    "flex items-center rounded border px-4 py-2",
                    theme.uwhcAssistant.inputBorder,
                    theme.myVesselsTableRow.row.primary,
                    theme.headerComponent.tailwind.buttonText,
                  )}
                  onClick={goToPrev}
                >
                  Previous
                </button>
              )}

              {step < 11 && (
                <button
                  className={twMerge(
                    "rounded border px-4 py-2",
                    theme.uwhcAssistant.sendBg,
                    theme.uwhcAssistant.sendText,
                    theme.uwhcAssistant.inputBorder,
                  )}
                  onClick={() => goToNext()}
                >
                  Next
                </button>
              )}
            </div>
          </footer>
        )}

        <PageFooter />
      </div>
    </div>
  );
};
